import React, {FunctionComponent, useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../../Config/Hooks";
import {GenerationRateInverter, getEntities} from "./types/GenerationRateOfInvertersReducer";
import {RootState} from "../../../../Config/Store";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileCsv } from "@fortawesome/free-solid-svg-icons";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import IconButton from "@mui/material/IconButton";
import {ACard, AChartX, Period, PeriodSelector, Skeleton, APopoverIcon, YAxis, XAxis} from "@atiautomacao/ati-ui-library";
import {format} from "date-fns";
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {Avatar, Box, Button, Divider, Stack, Typography, useTheme} from "@mui/material";
import ColorInformation, {NewHtmlTooltip} from "../../../../Utils/ColorInformation";
import { DownloadOutlined, EnergySavingsLeafOutlined, InfoOutlined, SimCardDownloadOutlined } from "@mui/icons-material";
import themeDefaults from "../../../../Shared/Theme/ThemeDefaults";

const GenerationRateOfInvertersChart: FunctionComponent<{ skidId: any }> = ({skidId}) => {

    const dispatch = useAppDispatch();
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const generationRateInverter: Array<GenerationRateInverter> = useAppSelector((state: RootState) => state.generationRateByInverter.entities);
    const { loading } = useAppSelector((state: RootState) => state.generationRateByInverter);
    const theme = useTheme();
    const initialPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };

    const [period, setPeriod] = useState<Period>(initialPeriod);
    const [seriesData, setSeriesData] = useState<any[]>([])
    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<YAxis>();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    
    const formatCaption = (fromDateTime: Date, toDateTime: Date): string => {
        let dateIntervalOfCaption;
        if (period.groupBy && (period.groupBy.valueOf() === "none" || period.groupBy.valueOf() === "week" || period.groupBy.valueOf() === "month")) {
            dateIntervalOfCaption = format(fromDateTime, 'dd/MM') + ' a ' + format(toDateTime, 'dd/MM')
        } else {
            dateIntervalOfCaption = format(fromDateTime, 'HH:mm') + ' a ' + format(toDateTime, 'HH:mm')
        }

        return dateIntervalOfCaption;
    }

    const dateByPeriod = (): string => {
        let fromDateTime = new Date();
        let toDateTime = new Date();
        let captionData: string;

        if (period.fromDateTime) {
            fromDateTime = period.fromDateTime;
        }
        if (period.toDateTime) {
            toDateTime = period.toDateTime;
        }

        captionData = formatCaption(fromDateTime, toDateTime);
        return captionData;
    }
    useEffect(() => {
        setSeriesData(Array.isArray(generationRateInverter)
            ? generationRateInverter.map(item => {
                let truncatedRate;
                truncatedRate = item.generationRate;
                return item.generationRate !== undefined && item.generationRate !== null
                    ? truncateNumber(truncatedRate, 2)
                    : '-';
            })
            : []
        )
        setXAxis({
            type: 'category',
            data: Array.isArray(generationRateInverter)
                ? generationRateInverter.map(item => {
                    if (item.equipmentName !== undefined && item.equipmentName !== null) {
                        return item.equipmentName;
                    }else{
                        return ""
                    }
                })
                : [],

        })

        setYAxis({
            type: 'value',
            min: 0,
            max: yAxisMax,
            interval: truncateNumber(yAxisMax / 5),
            axisLabel: {
                formatter: function (value: any) {
                    return value.toFixed(2);
                },
            },
        })
    }, [generationRateInverter]);

    useEffect(() => {
        setSeriesData([])
    }, [skidId, period]);

    const maxAxisY = Math.max(
        ...(generationRateInverter || []).map(item => item.generationRate ?? 0)
    );

    const yAxisMax = maxAxisY > 100 ? 120 : 100;


    const datePeriodTime: any = dateByPeriod();


    const chartOption: any = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            },
            valueFormatter: (value: any) => `${value} %`,
        },
        toolbox: {
            feature: {
                dataView: {show: false, readOnly: false},
                magicType: {show: false, type: ['line', 'bar']},
                restore: {show: false}
            }
        },
        xAxis: xAxis,
        yAxis: yAxis,
        legend: {
            color: 'gray',
            show: true,
            type: 'scroll',
            bottom: 2,
            left: 'center',
            orient: 'horizontal',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1,
                color: "gray" // Mantém a cor do item
            },
            data: ['Taxa de Geração']
        },
        grid: {
            containLabel: true,
            top: 10,
            bottom: 40,
            right: 50,
            left: 0,
        },
        label: {
            precision: 1,
            position: 'right',
            valueAnimation: true,
            fontFamily: 'monospace'
        },
        animationDuration: 2000,
        series: {
            name: 'Taxa de Geração',
            data: seriesData,
            type: 'bar',
            unity: '%',
            date: datePeriodTime,
            itemStyle: {
                color: function (params: any) {
                    const value = params.data;
                    if (value < 60) {
                        return '#E56C6C';
                    } else if (value >= 60 && value <= 80) {
                        return '#F9D37F';
                    } else {
                        return '#91CC75';
                    }
                }
            }
        }
    };



    useEffect(() => {
        if(checkDiffDays(period.toDateTime, period.fromDateTime)){
        dispatch(getEntities({period: period, skidId: skidId}));
        }
    }, [dispatch, period, skidId]);

    useInterval(() => {
        if(checkDiffDays(period.toDateTime, period.fromDateTime)){
            dispatch(getEntities({period: period, skidId: skidId}));
        }
    }, userConfig.mainUpdateInterval) // 5 minutes


    let content;

    if (loading) {
        content = <Skeleton animation="wave" height={363} variant="rounded" width="100%" />;
    } else if (generationRateInverter) {
        content = <AChartX key={`chart-skid-ration-${openSubMenu}`} height={363} width="100%" option={chartOption} theme={theme.palette.mode}/>;
    }


    return (
        <div style={{ position: 'relative' }}>
            <ACard
                key={`chart-skid-ration`}
                headerControlsPosition={"header"}
                styleProps={{
                    cardStyle: { height: 463 },
                    contentStyle: { padding: '0 16px', height: 408, alignItems: 'center' },
                    subTitleStyle: { display: 'none'},
                }}
                title={"Taxa de Geração"}
                ariaLabel="dashboard-skid-ration-chart"
                headerActions={
                <Stack direction="column" spacing={"8px"}>
                    <Box sx={{ display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 12px'}}>
                        <IconButton disabled={true} size="small" >
                            <SimCardDownloadOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Baixar como CSV</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                </Stack>
            }
                InformationTooltip={ 
                    <NewHtmlTooltip
                        style={{alignItems: 'center', display: 'flex', padding: '8px'}}
                        title={
                            <ColorInformation />
                        }>
                        <Button>HTML</Button>
                    </NewHtmlTooltip>
                }
                headerControls={
                     <Stack direction="row" spacing={2}>
                        <PeriodSelector
                        styleProps={{ base: { flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0' } }}
                        mode="hybrid"
                        hideDatetime={false}
                        hideGroup={false}
                        disableHourSelector={true}
                        inputFormat={'dd/MM/yyyy'}
                        periodInterval={{
                            day: { date: period.toDateTime, startTime: "00:00:00", endTime: "23:59:59" },
                            week: { startTime: "00:00:00", endTime: "23:59:59", startInterval: 6 },
                            month: { startTime: "00:00:00", endTime: "23:59:59", startInterval: 30 }
                        }}
                        period={period}
                        onChange={setPeriod}
                        groupByOptions={["day", "week", "month"]}
                    />
                    </Stack>
                }
            >
                {seriesData && seriesData.length > 0 ? content :
                    <DataNotFound boxStyle={{ height: 363, width: '100%' }} />}
            </ACard>
        </div>
    );

};

export default GenerationRateOfInvertersChart;